import { ReadTemplateSummary } from '@/services/api/generated/templatesV2Service';

import { Address, AddressPayload } from './address-types';
import { User } from './user-types';

// Firm status enum
export enum FirmStatus {
  ACTIVE = 'Active',
  TESTING = 'Testing',
  SOFTWARE_ONLY = 'Software Only',
  NEW_LEAD = 'New Lead',
  OPEN = 'Open',
  CLOSED = 'Closed',
}

// Firm type enum
export enum FirmType {
  SOLO = 'Solo',
  SMALL = 'Small',
  MEDIUM = 'Medium',
  ENTERPRISE = 'Enterprise',
}

// Time zone enum
export enum TimeZone {
  PST = 'PST',
  MST = 'MST',
  CST = 'CST',
  EST = 'EST',
  AKST = 'AKST',
  AST = 'AST',
  HST = 'HST',
}

// MedRecs Branding Mode enum
export enum MedRecsBrandingMode {
  CUSTOM = 'custom',
  FIRM = 'firm',
  FINCH = 'finch',
}

// CMS System enum
export enum CMSSystem {
  CASEPEER = 'CASEpeer',
  FILEVINE = 'Filevine',
  LITIFY = 'Litify',
  MYCASE = 'MyCase',
  CLIO_MANAGE = 'Clio Manage',
  SMARTADVOCATE = 'SmartAdvocate',
  PRACTICEPANTHER = 'PracticePanther',
  NEEDLES = 'Needles',
  NEOS = 'Neos',
  MERUS = 'Merus',
  TRIALWORKS = 'TrialWorks',
  LAWMATICS = 'Lawmatics',
}

// Lead System enum
export enum LeadSystem {
  // Dedicated lead systems
  LEADDOCKET = 'LeadDocket',
  LAWPRONATION = 'LawProNation',
  CLIOGROW = 'ClioGrow',
  INTAKER = 'Intaker',
  LAWMATICS = 'Lawmatics',
  // CMS systems that can also track leads
  CASEPEER = 'CASEpeer',
  FILEVINE = 'Filevine',
  LITIFY = 'Litify',
  MYCASE = 'MyCase',
  CLIO_MANAGE = 'Clio Manage',
  SMARTADVOCATE = 'SmartAdvocate',
  PRACTICEPANTHER = 'PracticePanther',
  NEEDLES = 'Needles',
  NEOS = 'Neos',
  MERUS = 'Merus',
  TRIALWORKS = 'TrialWorks',
}

// CMS Type enum (kept for backwards compatibility)
export enum CMSType {
  CLIO = 'Clio',
  FILEVINE = 'FileVine',
  NONE = 'None',
}

// Intake Preferences interfaces (matches backend schema)
export interface IntakePreferenceSection {
  title: string;
  content: string;
}

// Qualification criteria types for intake decision support
export type QualificationAction = 'accept' | 'reject' | 'escalate';

export interface QualificationCriteria {
  date_of_loss_max_days: number | null;
  accepted_states: string[];
  case_type_actions: Record<string, QualificationAction>;
  default_case_type_action: QualificationAction;
}

export interface IntakePreferences {
  sections: IntakePreferenceSection[];
  qualification_criteria?: QualificationCriteria;
}

export interface FirmBillingConfig {
  contracted_case_count?: number | null;
}

// Case Manager Assignment Strategy type
export type CaseManagerAssignmentStrategy = 'round_robin' | 'disabled';

// Complete Firm model interface
export interface Firm {
  id: string;
  name: string;
  full_name?: string;
  filevine_org_id?: number;
  email?: string;
  adobe_sign_email?: string;
  status?: FirmStatus;
  type?: FirmType;
  time_zone?: TimeZone;
  primary_state?: string;
  created_at: string; // ISO date string format
  updated_at: string; // ISO date string format
  logo_url?: string | null;
  letterhead_url?: string | null;
  firm_footer_url?: string | null;
  firm_logo_url?: string | null;
  firm_watermark_url?: string | null;
  // Raw (original upload) collateral URLs
  raw_letterhead_url?: string | null;
  raw_firm_footer_url?: string | null;
  raw_firm_logo_url?: string | null;
  raw_firm_watermark_url?: string | null;
  // Cleaned (background-removed) collateral URLs
  cleaned_letterhead_url?: string | null;
  cleaned_firm_footer_url?: string | null;
  cleaned_firm_logo_url?: string | null;
  cleaned_firm_watermark_url?: string | null;
  hipaa_cover_sheet_template?: string | null; // Backwards compatible - single template
  hipaa_attorney_relationship_form_url?: string | null;
  hipaa_reproductive_rights_attestation_url?: string | null;
  // NEW: Multiple HIPAA templates support
  hipaa_cover_sheet_templates?: ReadTemplateSummary[];
  default_hipaa_template_id?: string | null; // Computed from is_default
  // CMS tracking fields (legacy)
  cms_type?: CMSType | null;
  cms_subdomain?: string | null;
  // New CMS and Lead System tracking
  cms_system?: CMSSystem | null;
  cms_link?: string | null;
  onepassword_link?: string | null; // Computed from OnePassword integration
  lead_system?: LeadSystem | null;
  lead_system_link?: string | null;
  // Operational preferences and notes
  intake_preferences?: IntakePreferences | null;
  billing_config?: FirmBillingConfig | null;
  case_preferences?: string | null;
  commentary?: string | null;
  errors?: string | null;
  plan_to_derisk?: string | null;
  sop?: string | null;
  // Staff assignment
  managing_paralegal_id?: string | null;
  managing_paralegal?: User | null;
  // Front channel selection
  front_channel?: {
    front_id: string;
    name?: string | null;
    address?: string | null;
  } | null;
  // SMS sender number for treatment SMS (Dialpad number in E.164 format)
  sms_sender_number?: string | null;
  // SMS sender name (display name for the sender)
  sms_sender_name?: string | null;
  // Firm fax number
  fax?: string | null;
  // Employer Identification Number (EIN) in XX-XXXXXXX format
  ein?: string | null;
  // Firm address
  address?: Address | null;
  // Feature flags for this firm
  feature_flags?: Record<string, string>;
  // Case Manager Assignment Strategy (for auto-assigning case managers to new matters)
  case_manager_assignment_strategy?: CaseManagerAssignmentStrategy;
  // Client Portal white-labeling fields
  client_portal_subdomain?: string | null;
  client_portal_logo_url?: string | null;
  client_portal_primary_color?: string | null;
  client_portal_secondary_color?: string | null;

  // Header/footer DOCX for generic template swap
  firm_header_footer_docx_url?: string | null;

  // MedRecs branding mode
  medrecs_branding_mode?: MedRecsBrandingMode;

  // External task rule engine settings
  auto_backfill_external_task_rules_on_import?: boolean;
}

// For filtering firms in list view
export interface FirmFilters {
  search: string;
  status?: string;
  sortBy: 'name' | 'updated_at' | 'created_at';
  sortDirection: 'asc' | 'desc';
}

// For creating a new firm
export interface CreateFirmPayload {
  name: string;
  full_name?: string;
  email?: string;
  adobe_sign_email?: string;
  filevine_org_id?: number;
  status?: FirmStatus;
  type?: FirmType;
  time_zone?: TimeZone;
  primary_state?: string;
  hipaa_cover_sheet_template?: string | null;
  hipaa_attorney_relationship_form_url?: string | null;
  hipaa_reproductive_rights_attestation_url?: string | null;
  // CMS tracking fields (legacy)
  cms_type?: CMSType | null;
  cms_subdomain?: string | null;
  // New CMS and Lead System tracking
  cms_system?: CMSSystem | null;
  cms_link?: string | null;
  lead_system?: LeadSystem | null;
  lead_system_link?: string | null;
  // Operational preferences and notes
  billing_config?: FirmBillingConfig | null;
  case_preferences?: string | null;
  commentary?: string | null;
  errors?: string | null;
  plan_to_derisk?: string | null;
  sop?: string | null;
  // Staff assignment
  managing_paralegal_id?: string | null;
  // Front channel selection
  front_channel_front_id?: string | null;
  // SMS sender number for treatment SMS (Dialpad number in E.164 format)
  sms_sender_number?: string | null;
  // SMS sender name (display name for the sender)
  sms_sender_name?: string | null;
  // Firm fax number
  fax?: string | null;
  // Employer Identification Number (EIN) in XX-XXXXXXX format
  ein?: string | null;
  // Firm address - can pass nested object for create/update or address_id for linking
  address?: AddressPayload | null;
  address_id?: string | null;

  // MedRecs branding mode
  medrecs_branding_mode?: MedRecsBrandingMode;

  // External task rule engine settings
  auto_backfill_external_task_rules_on_import?: boolean;
}

// For updating a firm
export interface UpdateFirmPayload extends Partial<CreateFirmPayload> {
  id: string;
  intake_preferences?: IntakePreferences | null;
  case_manager_assignment_strategy?: CaseManagerAssignmentStrategy;
  // Client Portal white-labeling fields
  client_portal_subdomain?: string | null;
  client_portal_primary_color?: string | null;
  client_portal_secondary_color?: string | null;
}

